/**
 * SITE.STORE.TS
 */
import { createEntityStore } from "@/core/factory/store"
import { createEmptySite, Site, SITE_ENTITY } from "./site.types"
import { addPopulation, addRisk, removePopulation, removeRisk, resolveOverlaps, select, updateRisk } from "./site.thunk"
import { createSelector } from "@reduxjs/toolkit"
import { formatSite } from "./site.formatter"
import { AccountRisksSettings } from "@/redux/account.types"
import { Map, MAP_ENTITY } from "@/entities/map/model/map.types"
import { CustomEntityState } from "@/core/factory/types"
import { PhysicalRisk } from "@/entities/physical-risk/model/physical-risk.types"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import { ESTABLISHMENT_ENTITY, Establishment } from "@/entities/establishment/model/establishment.types"

export type SiteContext = {
  accountRisksSettings: AccountRisksSettings
  physicalRisks: PhysicalRisk[]
}

const siteContextSelector = createSelector(
  [(state: any) => state._session.accountRisksSettings, physicalRiskStore.selectors.list],
  (accountRisksSettings, physicalRisks) => ({
    accountRisksSettings,
    physicalRisks
  })
)

const createSiteStore = () => {
  const base = createEntityStore<Site, SiteContext>(SITE_ENTITY, createEmptySite, siteContextSelector, formatSite)

  /**
   * List
   * List is sorted according map
   */
  const listForMap = createSelector(
    base.selectors.list,
    (s) => s[MAP_ENTITY] as CustomEntityState<Map>,
    (sites: Site[], mapSlice) => {
      const { active: map } = mapSlice
      return sites
        .filter((site) => site.MapId === map.id)
        .sort((a, b) => map.sitesOrder.indexOf(a.id) - map.sitesOrder.indexOf(b.id))
    }
  )

  /**
   * Get all sites for one risk
   */
  const listForRisk = createSelector(
    [base.selectors.list, (_: any, risk?: PhysicalRisk) => risk, physicalRiskStore.selectors.draft],
    (sites: Site[], risk?: PhysicalRisk, draftRisk?: PhysicalRisk) => {
      const r = risk ?? draftRisk
      if (!r) return []
      return sites.filter((x) => r.siteIds.includes(x.id))
    }
  )

  const listForActiveEstablishment = createSelector(
    [base.selectors.list, (state) => state[ESTABLISHMENT_ENTITY].active as Establishment],
    (sites, activeEstablishment) => {
      if (!activeEstablishment?.id) return sites
      return sites.filter((site) => site.EstablishmentId === activeEstablishment.id)
    }
  )

  /**
   * Sites not attached to any map
   */
  const withoutMap = createSelector(base.selectors.list, (sites: Site[]) => sites.filter((site) => !site.MapId))

  /**
   * Index of the active site
   */
  const activeIndex = createSelector(listForMap, base.selectors.active, (listForMap, active) => {
    return listForMap.findIndex((x) => active.id === x.id) + 1
  })

  return {
    ...base,
    selectors: {
      ...base.selectors,
      withoutMap,
      activeIndex,
      listForActiveEstablishment,
      listForMap,
      listForRisk
    },
    thunks: {
      ...base.thunks,
      addPopulation,
      addRisk,
      select,
      removePopulation,
      removeRisk,
      resolveOverlaps,
      updateRisk
    }
  }
}

const siteStore = createSiteStore()
export default siteStore
